/**
 * @author omriha
 * 05/02/2020
 */
import React, {useMemo} from 'react';
import {connect} from 'react-redux';
import {push} from 'connected-react-router';
import {useTranslation} from 'react-i18next';
import queryString from 'query-string';
import SMCPage from '../../../components/core/pageComponents/SMCPage';
import ButtonContainer from '../../../components/Simple/ButtonContainer';
import {useEditorDimensions} from '../../../utils/hooks';
import LabelContainer from '../../../components/Page/LabelContainer';
import SMCEditor from '../../../components/Editor';
import {useSystemUserEditor} from './SystemUserEditorHelper';
import SMCButton from '../../../components/Simple/SMCButton';
import RouteLeavingGuard from '../../PopupPages/RouteLeavingGuard';
import {isUserAdminSelector, isSystemUserSelector, systemUserPermissionsSelector, isAdminReadOnlySelector} from '../../../store/selectors/user.selectors';
import {SMCDisabledProvider} from '../../../components/Simple/SMCDisabledContext';
import VerifyButtonsContainer from "../VerifyButtonsContainer";
import ButtonsContainerStyles from "../ButtonsContainerStyles";


/**
 * High level element to fetch, edit and save system user, all logic implemented inside.
 * @param {string} id Id of system user to fetch from the server to edit
 */
function SystemUserEditor({
    navigateTo,
    id,
    urlParams,
    isUserAdmin,
    isAdminReadOnly,
    isSystemUser,
    systemUserPermissions
}) {
    const { t } = useTranslation();
    const { editorWidth, editorHeight } = useEditorDimensions();
    const classes = ButtonsContainerStyles();

    let defaultPage = 'general';
    const queryParams = queryString.parse(urlParams);
    if (queryParams && queryParams['page']) {
        defaultPage = queryParams['page'];
    }

    const {
        systemUserObject,
        isObjectDirty,
        editorPages,
        helpId,
        origSystemUserObject,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setSystemUserData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData,
        availableSystemRoles,
        minimumPasswordLength,
        maximumPasswordLength
    } = useSystemUserEditor(id, defaultPage);
    
    // Data passed down to the editor pages
    const editorPageData = {
        systemUserObject,
        origSystemUserObject,
        setEditorPageId: onPageChange,
        setSystemUserData,
        isPageDataLoaded,
        availableSystemRoles,
        minimumPasswordLength,
        maximumPasswordLength
    }
    
    const canDelete = useMemo(() => {
        // System users can't delete
        if (isSystemUser) {
            return false;
        }
        if (isUserAdmin) {
            return !isAdminReadOnly;
        }
        if (systemUserPermissions) {
            return systemUserPermissions.delete;
        }
        return false;
    }, [isSystemUser, isUserAdmin, isAdminReadOnly, systemUserPermissions]);
    
    const canModify = useMemo(() => {
        if (isUserAdmin) {
            return !isAdminReadOnly;
        }
        if (systemUserPermissions) {
            return systemUserPermissions.modify;
        }
        return false;
    }, [isUserAdmin, isAdminReadOnly, systemUserPermissions]);

    return (
        <>
        <RouteLeavingGuard when={isObjectDirty} />
        <SMCPage loading={!isPageDataLoaded} helpId={helpId}>
            <div className="ObjectEditor" style={{ width: editorWidth }}>
                {/* Header */}
                <div className="ObjectEditorHeader">
                    <ButtonContainer>
                        <SMCButton labelClassName={classes.labelColor} icon="delete" variant="ghost" disabled={!isPageDataLoaded || !canDelete} onClick={deleteButtonOnClick}>{t('SMC_GEN_DELETE')}</SMCButton>
                    </ButtonContainer>
                    <LabelContainer label="Name:" value={id} />
                </div>
                <SMCDisabledProvider forceDisable={!canModify}>
                    <SMCEditor
                        editorPageData={editorPageData}
                        height={editorHeight}
                        width={editorWidth}
                        errorListByPage={errorMessagesByPageList}
                        chosenPageId={chosenPageId}
                        onPageChange={onPageChange}
                        isPageLoaded={isPageDataLoaded && !isInnerPageLoading}
                        isPageLoadFailed={isPageLoadFailed}
                        reloadData={reloadPageData}
                        pages={editorPages}
                    />
                </SMCDisabledProvider>
            </div>
        </SMCPage>
            <VerifyButtonsContainer
                disabled={!isPageDataLoaded || !canModify}
                onClickSaveButton={saveButtonOnClick}
                onClickCancelButton={revertButtonOnClick}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    isUserAdmin: isUserAdminSelector(state),
    isAdminReadOnly: isAdminReadOnlySelector(state),
    isSystemUser: isSystemUserSelector(state),
    systemUserPermissions: systemUserPermissionsSelector(state),
    urlParams: state.router.location.search
});

const mapDispatchToProps = {
    navigateTo: push
}

export default connect(mapStateToProps, mapDispatchToProps)(SystemUserEditor);